import _ from "lodash";
import {useVaultUpdate} from "../../core/util/useVaultUpdate";
import {atParentStudents} from "../../core/vault/StateCursors";
import Constants from "../../../shared/Constants.js";

const PLACEHOLDER_NAME_PATTERN = /^Student [A-Z]+$/i;

const shouldSkipDisplayNameStep = (studentData: any): boolean => {
	const name = studentData?.displayName?.trim() || "";
	return !PLACEHOLDER_NAME_PATTERN.test(name);
};

// List of primary setup steps in order
export const STUDENT_PRIMARY_SETUP_STEPS = [
	"START",
	"DISPLAY_NAME",
	"AGE",
	"BA_LEVEL",
	"USERNAME_SELECTION",
	"USERNAME_CONFIRMATION",
	"PASSWORD_SELECTION",
	"FINAL",
] as const;

export type StudentPrimarySetupStep = typeof STUDENT_PRIMARY_SETUP_STEPS[number];

// Map from step name to index
export const STUDENT_PRIMARY_SETUP_INDEX_BY_STEP = _.mapValues(
	_.invert(STUDENT_PRIMARY_SETUP_STEPS),
	index => parseInt(index)
) as Record<StudentPrimarySetupStep, number>;

export const getPrimarySetupStepByIndex = (
	index: number
): StudentPrimarySetupStep => {
	return STUDENT_PRIMARY_SETUP_STEPS[index] as StudentPrimarySetupStep;
};

// Function from step name to next step name
export const getNextPrimarySetupStep = (
	currentStep: StudentPrimarySetupStep
): StudentPrimarySetupStep | null => {
	if (currentStep === "FINAL") {
		return null;
	}
	return getPrimarySetupStepByIndex(
		STUDENT_PRIMARY_SETUP_INDEX_BY_STEP[currentStep] + 1
	);
};

// Function from step name to previous step name
export const getPreviousPrimarySetupStep = (
	currentStep: StudentPrimarySetupStep,
	studentData: any
): StudentPrimarySetupStep | null => {
	if (currentStep === "START") {
		return null;
	}
	if (studentData.username && currentStep === "USERNAME_CONFIRMATION") {
		return "BA_LEVEL";
	}
	const prevStep = getPrimarySetupStepByIndex(
		STUDENT_PRIMARY_SETUP_INDEX_BY_STEP[currentStep] - 1
	);
	if (prevStep === "DISPLAY_NAME" && shouldSkipDisplayNameStep(studentData)) {
		return "START";
	}
	return prevStep;
};

// Function from progress bar index to step name, for handling clicks on the progress bar
export const getPrimarySetupStepByProgressBarSection = (
	sectionIndex: number
): StudentPrimarySetupStep => {
	switch (sectionIndex) {
		// TODO: Check with Stella about these
		case 0:
			return "AGE";
		case 1:
			return "USERNAME_SELECTION";
		case 2:
			return "PASSWORD_SELECTION";
		default:
			return "AGE";
	}
};

// Function from step name to progress section index
export const getProgressSectionByStep = (
	step: StudentPrimarySetupStep
): number => {
	switch (step) {
		case "START":
			return 0;
		case "DISPLAY_NAME":
			return 0.25;
		case "AGE":
			return 0.5;
		case "BA_LEVEL":
			return 1;
		case "USERNAME_SELECTION":
			return 1.5;
		case "USERNAME_CONFIRMATION":
			return 2;
		case "PASSWORD_SELECTION":
			return 2.5;
		case "FINAL":
			return 3;
		default:
			return 0;
	}
};

// Hook to get unregistered students from the vault
export const useUnregisteredStudents = (): any[] => {
	const students = useVaultUpdate({
		getValuesFromVault: vault => atParentStudents(vault).get(),
	});

	return students
		? Object.values(students).filter((student: any) =>
				isUnregisteredStudent(student)
		  )
		: [];
};

export const isUnregisteredStudent = (student: any): boolean => {
	return !student.hasCompletedRegistration && student.isActive;
};

export const transformStudentDataToStudent = (studentData: any): any => {
	if (!studentData) return null;

	const studentID = studentData.studentID || studentData.id;

	return {
		id: studentID,
		displayName: studentData.displayName,
		username: studentData.username,
		avatarData: studentData.avatarData || {},
		hasRegistered: studentData.hasRegistered || false,
	};
};

// Name validation utilities for student registration
const ALLOWED_NAME_CHARS =
	"abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ -";
const ALLOWED_NAME_PATTERN = new RegExp(`^[${ALLOWED_NAME_CHARS}]*$`);

/**
 * Cleans and validates a name. Returns null if the name has invalid characters.
 * Otherwise returns the cleaned name (lowercase, letters only for comparison).
 */
export const cleanStudentName = (name: string): string | null => {
	if (!ALLOWED_NAME_PATTERN.test(name)) return null;
	return name.replace(/[^a-zA-Z]/g, "").toLowerCase();
};

/** Options for {@link isValidStudentName}. */
export type IsValidStudentNameOptions = {
	/** Name already passed through {@link cleanStudentName} (letters only, lowercase). */
	isCleaned?: boolean;
	/** Student whose name is being set; not compared against itself. */
	currentStudentID?: number;
	/** Other students on the parent account (siblings). */
	siblings?: Array<{studentID: number; displayName?: string | null}>;
};

/**
 * Determines if a name is valid for registration.
 * @param name - The name to validate
 * @returns Error type string, or true if no error
 */
export const isValidStudentName = (
	name: string,
	options?: IsValidStudentNameOptions
): string | boolean => {
	if (!name) return "E_MISSING";

	const isCleaned = options?.isCleaned ?? false;
	const cleanedName = isCleaned ? name : cleanStudentName(name);
	if (!cleanedName) return "E_BAD_CHAR";
	if (name.length > Constants.MAX_DISPLAY_NAME_LENGTH) return "E_LENGTH";

	const currentID = options?.currentStudentID;
	const siblings = options?.siblings;
	if (currentID != null && siblings?.length) {
		const currentIdNum = Number(currentID);
		for (const s of siblings) {
			if (Number(s.studentID) === currentIdNum) continue;
			const raw = s.displayName?.trim();
			if (!raw) continue;
			const cleanedSibling = cleanStudentName(raw);
			if (cleanedSibling && cleanedSibling === cleanedName) {
				return "E_DUPLICATE_SIBLING";
			}
		}
	}

	return true;
};

/** True for default names like "Student A" / "student a" (not a real chosen name). */
export const isPlaceholderStudentDisplayName = (name: string): boolean =>
	PLACEHOLDER_NAME_PATTERN.test(name.trim());
